import Editor from "@monaco-editor/react";
import type { ReactNode } from "react";

import { FILE_PREVIEW_MONACO_OPTIONS } from "../../code-editor/filePreviewMonacoOptions";
import {
  BinarySkillFilePreview,
  isBinarySkillFileContent,
} from "../BinarySkillFilePreview";
import { CsvSkillFilePreview } from "../CsvSkillFilePreview";
import { OpenaiYamlFormEditor } from "../OpenaiYamlForm";
import { getLanguageForPath } from "../skill-file-support";
import { SkillMarkdownPreview } from "../SkillMarkdownPreview";
import type { SkillMarkdownPreviewCacheEntry } from "../SkillMarkdownPreview";

import { isOpenaiYamlFilePath } from "./openai-yaml-path";
import { OpenaiYamlPreviewMode } from "./openai-yaml-preview-mode";
import { SkillCollectionViewerEmptyNoFiles, SkillCollectionViewerEmptySelectFile } from "./SkillCollectionViewerEmptyStates";

enum PreviewSurfaceKind {
  Binary = "binary",
  Custom = "custom",
  Csv = "csv",
  EmptyNoFiles = "emptyNoFiles",
  EmptySelectFile = "emptySelectFile",
  Markdown = "markdown",
  OpenaiYamlForm = "openaiYamlForm",
  OpenaiYamlRaw = "openaiYamlRaw",
  PlainMonaco = "plainMonaco",
}

const resolvePreviewSurfaceKind = (params: {
  isBinary: boolean;
  isCsv: boolean;
  hasCustomPreview: boolean;
  filePathsLength: number;
  selectedPath: string | null;
  isMarkdown: boolean;
  openaiYamlPreviewMode: OpenaiYamlPreviewMode;
}): PreviewSurfaceKind => {
  if (params.hasCustomPreview) {
    return PreviewSurfaceKind.Custom;
  }

  if (params.filePathsLength === 0) {
    return PreviewSurfaceKind.EmptyNoFiles;
  }

  if (!params.selectedPath) {
    return PreviewSurfaceKind.EmptySelectFile;
  }

  if (params.isBinary) {
    return PreviewSurfaceKind.Binary;
  }

  if (params.isCsv) {
    return PreviewSurfaceKind.Csv;
  }

  if (isOpenaiYamlFilePath(params.selectedPath)) {
    switch (params.openaiYamlPreviewMode) {
      case OpenaiYamlPreviewMode.Form:
        return PreviewSurfaceKind.OpenaiYamlForm;
      case OpenaiYamlPreviewMode.Raw:
        return PreviewSurfaceKind.OpenaiYamlRaw;
      default:
        return PreviewSurfaceKind.OpenaiYamlForm;
    }
  }

  if (params.isMarkdown) {
    return PreviewSurfaceKind.Markdown;
  }

  return PreviewSurfaceKind.PlainMonaco;
};

export interface SkillCollectionViewerPreviewBodyProps {
  customPreview: ReactNode | undefined;
  filePathsLength: number;
  activeSelectedPath: string | null;
  selectedContent: string;
  isMarkdown: boolean;
  validFilePathSet: Set<string>;
  files: Record<string, string>;
  markdownPreviewByPath?: ReadonlyMap<string, SkillMarkdownPreviewCacheEntry>;
  setSelectedPath: (path: string) => void;
  openaiYamlPreviewMode: OpenaiYamlPreviewMode;
  openaiYamlContentVersionKey: string | number | null | undefined;
  licenseKey?: string;
}

export const SkillCollectionViewerPreviewBody = ({
  customPreview,
  filePathsLength,
  activeSelectedPath,
  selectedContent,
  isMarkdown,
  validFilePathSet,
  files,
  markdownPreviewByPath,
  setSelectedPath,
  openaiYamlPreviewMode,
  openaiYamlContentVersionKey,
  licenseKey,
}: SkillCollectionViewerPreviewBodyProps) => {
  const markdownPreparse =
    activeSelectedPath && isMarkdown ? markdownPreviewByPath?.get(activeSelectedPath) : undefined;
  const isBinary = isBinarySkillFileContent(selectedContent);
  const isCsv = activeSelectedPath?.toLowerCase().endsWith(".csv") ?? false;

  const hasCustomPreview = customPreview !== undefined;
  const surfaceKind = resolvePreviewSurfaceKind({
    isBinary,
    isCsv,
    hasCustomPreview,
    filePathsLength,
    selectedPath: activeSelectedPath,
    isMarkdown,
    openaiYamlPreviewMode,
  });

  switch (surfaceKind) {
    case PreviewSurfaceKind.Binary:
      return <BinarySkillFilePreview content={selectedContent} />;
    case PreviewSurfaceKind.Custom:
      return customPreview;
    case PreviewSurfaceKind.Csv:
      return <CsvSkillFilePreview content={selectedContent} licenseKey={licenseKey} />;
    case PreviewSurfaceKind.EmptyNoFiles:
      return <SkillCollectionViewerEmptyNoFiles />;
    case PreviewSurfaceKind.EmptySelectFile:
      return <SkillCollectionViewerEmptySelectFile />;
    case PreviewSurfaceKind.Markdown:
      return (
        <SkillMarkdownPreview
          key={activeSelectedPath ?? "md"}
          filePath={activeSelectedPath}
          markdown={selectedContent}
          markdownPreparse={markdownPreparse}
          hasLinkedFile={path => validFilePathSet.has(path)}
          getLinkedFileContent={path => files[path]}
          onNavigateToFile={setSelectedPath}
        />
      );
    case PreviewSurfaceKind.OpenaiYamlForm:
      return (
        <OpenaiYamlFormEditor
          key={`${activeSelectedPath ?? "yaml"}-${openaiYamlContentVersionKey ?? "live"}`}
          content={selectedContent}
          readOnly
        />
      );
    case PreviewSurfaceKind.OpenaiYamlRaw: {
      const language = activeSelectedPath ? getLanguageForPath(activeSelectedPath) : "plaintext";
      return (
        <Editor
          height="100%"
          defaultLanguage={language}
          language={language}
          value={selectedContent}
          options={FILE_PREVIEW_MONACO_OPTIONS}
        />
      );
    }
    case PreviewSurfaceKind.PlainMonaco: {
      const language = activeSelectedPath ? getLanguageForPath(activeSelectedPath) : "plaintext";
      return (
        <Editor
          height="100%"
          defaultLanguage={language}
          language={language}
          value={selectedContent}
          options={FILE_PREVIEW_MONACO_OPTIONS}
        />
      );
    }
    default:
      return <SkillCollectionViewerEmptySelectFile />;
  }
};
